import { useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowDown, ArrowUp, Download } from "lucide-react";

import {
  BANDS, CATEGORIES, CROP_STATUSES, downloadPageResolutionCsv, fetchPageResolution,
  type EditionRow, type ResParams,
} from "../api/pageResolution";
import { explainError } from "../api/client";
import { Spinner } from "../components/Spinner";

/* ──────────────────────────────────────────────────────────────────────
   /page-resolution — image width of every crawled edition for one day.

   The cropper skips editions narrower than 1200 px (crop_min_width), so
   this is where to see which papers arrive too small to clip, by
   category and width band. Filter state lives in the URL. SuperAdmin +
   Admin — RequireGroup on the route, require_group on the API.
   ────────────────────────────────────────────────────────────────────── */

type SortKey = "publisher" | "edition" | "language" | "category" | "page_count" | "min_width" | "max_width" | "max_height" | "band" | "crop_status";

const COLS: { key: SortKey; label: string; num?: boolean }[] = [
  { key: "publisher", label: "Publisher" },
  { key: "edition", label: "Edition" },
  { key: "language", label: "Language" },
  { key: "category", label: "Category" },
  { key: "page_count", label: "Pages", num: true },
  { key: "min_width", label: "Min W", num: true },
  { key: "max_width", label: "Max W", num: true },
  { key: "max_height", label: "Height", num: true },
  { key: "band", label: "Band" },
  { key: "crop_status", label: "Crop" },
];

const BAND_TONE: Record<string, string> = {
  "<600": "var(--accent)", "600–800": "#b7791f", "800–1200": "#8a6d1f", "≥1200": "#3f8f4f", unmeasured: "var(--fg-faint)",
};

function list(v: string | null): string[] {
  return (v || "").split(",").map((s) => s.trim()).filter(Boolean);
}

export function PageResolution() {
  const [sp, setSp] = useSearchParams();
  const params: ResParams = {
    date: sp.get("date") || undefined,
    category: list(sp.get("category")),
    band: list(sp.get("band")),
    crop: list(sp.get("crop")),
    language: sp.get("language") || undefined,
    publisher: sp.get("publisher") || undefined,
  };
  const [sort, setSort] = useState<{ key: SortKey; asc: boolean }>({ key: "max_width", asc: true });
  const [dl, setDl] = useState<string | null>(null);

  const res = useQuery({
    queryKey: ["page-resolution", params],
    queryFn: () => fetchPageResolution(params),
    placeholderData: keepPreviousData,
  });

  const set = (k: string, v: string | string[] | undefined) => {
    const n = new URLSearchParams(sp);
    const s = Array.isArray(v) ? v.join(",") : v;
    if (s) n.set(k, s); else n.delete(k);
    setSp(n, { replace: true });
  };
  const toggle = (k: "category" | "band" | "crop", v: string) => {
    const cur = params[k];
    set(k, cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
  };

  const rows = useMemo(() => {
    const r = [...(res.data?.rows || [])];
    const { key, asc } = sort;
    r.sort((a: EditionRow, b: EditionRow) => {
      const x = a[key] ?? -1, y = b[key] ?? -1;
      const c = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y));
      return asc ? c : -c;
    });
    return r;
  }, [res.data, sort]);

  const d = res.data;
  const summary = d?.summary;
  const cell = (b: string, c: string) => summary?.cells[b]?.[c];
  const bandTotal = (b: string) =>
    Object.values(summary?.cells[b] || {}).reduce((s, x) => ({ e: s.e + x.editions, p: s.p + x.pages }), { e: 0, p: 0 });

  return (
    <div className="flex flex-col gap-4 min-w-0">
      <header className="flex items-end justify-between flex-wrap gap-3 pb-2 border-b" style={{ borderColor: "var(--rule)" }}>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] mb-1">Print · Page resolution</div>
          <h1 className="font-display text-[26px] leading-tight">Page resolution</h1>
          <p className="font-sans text-[12px] text-[var(--fg-muted)] mt-1">
            Image width of every crawled edition. Pages narrower than {d?.crop_min_width ?? 1200} px are skipped by the cropper.
            Band = widest page of the edition.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={params.date || d?.date || ""} onChange={(e) => set("date", e.target.value)}
            className="bg-transparent border px-2 py-1 font-mono text-[12px]" style={{ borderColor: "var(--rule)" }} aria-label="Business date" />
          <button className="np-btn !px-3 !py-1.5" disabled={!!dl}
            onClick={async () => {
              setDl(null);
              try { setDl("…"); await downloadPageResolutionCsv({ ...params, date: params.date || d?.date }); setDl(null); }
              catch (e) { setDl(explainError(e)); setTimeout(() => setDl(null), 4000); }
            }}>
            <Download size={13} /> CSV
          </button>
        </div>
      </header>

      {d && (
        <div className="flex flex-wrap gap-3">
          <Tile label="editions crawled" value={d.totals.editions} />
          <Tile label="measured" value={d.totals.measured_editions} />
          <Tile label={`under ${d.crop_min_width} px`} value={d.totals.below_crop_min} tone="var(--accent)" />
          {BANDS.map((b) => (
            <button key={b} onClick={() => toggle("band", b)} aria-pressed={params.band.includes(b)}
              className="np-card px-4 py-2 text-left" style={params.band.includes(b) ? { outline: `2px solid ${BAND_TONE[b]}` } : undefined}>
              <div className="font-mono tabular-nums text-[18px]" style={{ color: BAND_TONE[b] }}>{bandTotal(b).e}</div>
              <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">{b} px · {bandTotal(b).p} pp</div>
            </button>
          ))}
        </div>
      )}

      {summary && (
        <div className="np-card overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--rule-strong)" }}>
                <Th>Band \ category</Th>
                {summary.categories.map((c) => <Th key={c} className="text-right">{c}</Th>)}
                <Th className="text-right">Total</Th>
              </tr>
            </thead>
            <tbody>
              {summary.bands.map((b) => (
                <tr key={b} style={{ borderTop: "1px solid var(--rule)" }}>
                  <Td><span className="font-mono" style={{ color: BAND_TONE[b] }}>{b}</span></Td>
                  {summary.categories.map((c) => {
                    const x = cell(b, c);
                    return (
                      <Td key={c} className="text-right font-mono tabular-nums">
                        {x ? (
                          <button className="hover:underline" title="Filter to this cell"
                            onClick={() => { const n = new URLSearchParams(sp); n.set("band", b); n.set("category", c); setSp(n, { replace: true }); }}>
                            {x.editions} <span className="text-[var(--fg-faint)] text-[11px]">/ {x.pages} pp</span>
                          </button>
                        ) : <span className="text-[var(--fg-faint)]">·</span>}
                      </Td>
                    );
                  })}
                  <Td className="text-right font-mono tabular-nums">{bandTotal(b).e} <span className="text-[var(--fg-faint)] text-[11px]">/ {bandTotal(b).p} pp</span></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="np-card px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-3">
        <Seg label="category" options={CATEGORIES} picked={params.category} onToggle={(v) => toggle("category", v)} />
        <Seg label="band" options={BANDS} picked={params.band} onToggle={(v) => toggle("band", v)} />
        <Seg label="crop" options={CROP_STATUSES} picked={params.crop} onToggle={(v) => toggle("crop", v)} />
        <label className="flex items-center gap-2 font-mono text-[11px] text-[var(--fg-muted)]">
          <span className="uppercase tracking-folio text-[10px] text-[var(--fg-faint)]">language</span>
          <select value={params.language || ""} onChange={(e) => set("language", e.target.value)}
            className="bg-transparent border px-2 py-1 font-mono text-[11px]" style={{ borderColor: "var(--rule)" }}>
            <option value="">all</option>
            {(d?.languages || []).map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </label>
        <input placeholder="publisher or edition…" defaultValue={params.publisher || ""}
          onKeyDown={(e) => { if (e.key === "Enter") set("publisher", (e.target as HTMLInputElement).value.trim()); }}
          onBlur={(e) => set("publisher", e.target.value.trim())}
          className="bg-transparent border px-2 py-1 font-sans text-[12px] min-w-[200px]" style={{ borderColor: "var(--rule)" }} />
        {(params.category.length || params.band.length || params.crop.length || params.language || params.publisher) ? (
          <button className="np-btn-ghost !px-3 !py-1" onClick={() => { const n = new URLSearchParams(); if (params.date) n.set("date", params.date); setSp(n, { replace: true }); }}>
            Clear filters
          </button>
        ) : null}
      </div>

      {dl && dl !== "…" && <div className="np-banner flex gap-2"><AlertTriangle size={14} className="text-[var(--accent)]" /> {dl}</div>}
      {res.isError && (
        <div className="np-banner flex items-start gap-2">
          <AlertTriangle size={14} className="text-[var(--accent)] shrink-0 mt-0.5" /> {explainError(res.error)}
        </div>
      )}
      {res.isLoading && <div className="py-16 flex justify-center"><Spinner label="Loading" /></div>}

      {d && (
        <div className={"np-card overflow-x-auto transition-opacity " + (res.isFetching ? "opacity-60" : "")}>
          <div className="px-3 py-2 font-mono text-[11px] text-[var(--fg-muted)]">{rows.length} editions · {d.date}</div>
          <table className="w-full text-[13px]">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--rule-strong)" }}>
                {COLS.map((c) => (
                  <Th key={c.key} className={c.num ? "text-right" : ""}>
                    <button className="inline-flex items-center gap-1 uppercase"
                      onClick={() => setSort((s) => ({ key: c.key, asc: s.key === c.key ? !s.asc : true }))}>
                      {c.label}
                      {sort.key === c.key && (sort.asc ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
                    </button>
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.issue_id} style={{ borderTop: "1px solid var(--rule)" }}>
                  <Td>{r.publisher}<span className="text-[var(--fg-faint)] font-mono text-[10px] ml-1">#{r.channel_id}</span></Td>
                  <Td>{r.edition}</Td>
                  <Td>{r.language}</Td>
                  <Td>{r.category}</Td>
                  <Td className="text-right font-mono tabular-nums">
                    {r.page_count ?? "—"}
                    {r.pages_measured !== (r.page_count ?? 0) && <span className="text-[var(--fg-faint)] text-[10px]"> ({r.pages_measured} m)</span>}
                  </Td>
                  <Td className="text-right font-mono tabular-nums">{r.min_width ?? "—"}</Td>
                  <Td className="text-right font-mono tabular-nums">{r.max_width ?? "—"}</Td>
                  <Td className="text-right font-mono tabular-nums">{r.max_height ?? "—"}</Td>
                  <Td><span className="font-mono text-[12px]" style={{ color: BAND_TONE[r.band] }}>{r.band}</span></Td>
                  <Td><span className="font-mono text-[12px]">{r.crop_status}</span></Td>
                </tr>
              ))}
              {!rows.length && <tr><Td className="text-[var(--fg-faint)] italic">No editions match.</Td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="np-card px-4 py-2">
      <div className="font-mono tabular-nums text-[18px]" style={tone ? { color: tone } : undefined}>{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">{label}</div>
    </div>
  );
}

function Seg({ label, options, picked, onToggle }: { label: string; options: readonly string[]; picked: string[]; onToggle: (v: string) => void }) {
  return (
    <div className="flex items-center gap-2">
      <span className="font-mono uppercase tracking-folio text-[10px] text-[var(--fg-faint)]">{label}</span>
      <div className="flex flex-wrap" role="group" aria-label={label}>
        {options.map((o) => (
          <button key={o} type="button" className="np-seg" aria-pressed={picked.includes(o)} onClick={() => onToggle(o)}>{o}</button>
        ))}
      </div>
    </div>
  );
}

function Th({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <th className={`text-left px-3 py-2 font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] ${className}`}>{children}</th>;
}

function Td({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>;
}
